// ConfirmModal.js - 재사용 가능한 확인 모달 컴포넌트
import React, { useEffect } from 'react';
import { FiAlertCircle, FiTrash2, FiX } from 'react-icons/fi';

function ConfirmModal({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  title = "확인", 
  message, 
  confirmText = "확인",
  cancelText = "취소",
  confirmColor = "#FF5252",
  icon = <FiAlertCircle size={24} color="#FF9800" />,
  theme = 'dark'
}) {
  // 키보드 지원 (Enter, Escape, Tab)
  useEffect(() => {
    if (!isOpen) return;
    
    const cancelButton = document.getElementById('confirm-modal-cancel-btn');
    const confirmButton = document.getElementById('confirm-modal-confirm-btn');
    
    // 모달이 열릴 때 취소 버튼에 포커스
    if (cancelButton) {
      setTimeout(() => cancelButton.focus(), 100);
    }
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // 현재 포커스된 버튼에 따라 동작
        if (document.activeElement === cancelButton) {
          onCancel();
        } else if (document.activeElement === confirmButton) {
          onConfirm();
        } else {
          // 포커스가 없으면 확인 버튼 동작
          onConfirm();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Tab') {
        // Tab 키는 기본 동작 허용 (버튼 간 이동)
        // Shift+Tab도 기본 동작 허용
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onConfirm, onCancel]);
  
  if (!isOpen) return null;

  const isDark = theme === 'dark';

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.2s ease-out'
  };

  const modalStyle = {
    backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    animation: 'slideIn 0.3s ease-out'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  };

  const titleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: isDark ? '#E0E0E0' : '#2C2C2C'
  };

  const messageStyle = {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: isDark ? '#B0B0B0' : '#666',
    lineHeight: '1.6'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  };

  const cancelButtonStyle = {
    padding: '10px 20px',
    backgroundColor: isDark ? '#333' : '#e8e8e8',
    color: isDark ? '#FAFAFA' : '#1a1a1a',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    outline: 'none',
  };

  const confirmButtonStyle = {
    padding: '10px 20px',
    backgroundColor: confirmColor,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    outline: 'none',
  };

  return (
    <>
      <div style={overlayStyle} onClick={onCancel}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            {icon}
            <h3 style={titleStyle}>{title}</h3>
          </div>
          
          <div style={messageStyle}>
            {message}
          </div>
          
          <div style={buttonContainerStyle}>
            <button
              id="confirm-modal-cancel-btn"
              onClick={onCancel}
              style={cancelButtonStyle}
              tabIndex={0}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${isDark ? '#555' : '#999'}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <FiX size={16} />
              {cancelText}
            </button>
            <button
              id="confirm-modal-confirm-btn"
              onClick={onConfirm}
              style={confirmButtonStyle}
              tabIndex={0}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${confirmColor}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <FiTrash2 size={16} />
              {confirmText}
            </button>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default ConfirmModal;

